import React, {useEffect, useState} from 'react';
import {Box, Button, Divider, Drawer, IconButton, InputLabel, Stack, Typography} from '@mui/material';
import {useNavigate, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {map} from 'rxjs';
import {Close} from '@icons/index';

import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';

import {Sizes} from '@constants/sizes.constants';
import {useSnackbar} from 'notistack';
import {clientsService} from '@store/clients';
import {ManageReport, reportQuery, ReportsFilters, reportsService} from '@store/reports';
import {TableFilter} from '@store/common/tableFilter.model';
import {useObservable} from '@ngneat/react-rxjs';
import DateRangePickerComponent from '@components/inputs/dateRangePicker/DateRangePicker.component';
import AsyncSelectWithSearchComponent from '@components/inputs/asyncSelect/AsyncSelectWithSearch.component';

interface ManageGenerateReportDrawerProps {
  fromClient?: boolean;
  fromSupplier?: boolean;
  isCreation?: boolean;
}

const ManageGenerateReportDrawer = (props: ManageGenerateReportDrawerProps) => {
  const {isCreation} = props;

  const {t} = useTranslation();
  const navigate = useNavigate();
  const {reportId} = useParams();
  const {enqueueSnackbar} = useSnackbar();

  const handleClose = () => navigate('..', {replace: true});

  const [loading, setLoading] = useState(false);
  const [filters] = useObservable(reportQuery.filters$);


  const [newFilters, setNewFilters] = useState<TableFilter[]>(filters.tableFilters);
  const [newFiltersDate] = useState<ReportsFilters>(filters);
  const [from, setFrom] = useState<Date | undefined | null>(undefined);
  const [to, setTo] = useState<Date | undefined | null>(undefined);

  const updateFilters = (name: string, filterIndex: number) => (value: any) => {
    const filtersToUpdate = [...(newFilters || [])];
    filtersToUpdate[filterIndex] = {...filtersToUpdate[filterIndex], [name]: value};
    setNewFilters(filtersToUpdate);
  };

  const [report, setReport] = useState<any>(isCreation ? {
    clientOrganisation: undefined,
    clientOrganisationUnit: undefined,
    supplierOrganisationUnit: undefined,
    wasteFamily: undefined,
    waste: undefined,
    container: undefined,
    startDate: undefined,
    endDate: undefined,

  } : {});

  useEffect(() => {
    if (reportId) {
      reportsService.getSelectedReport(reportId).subscribe({
        complete: () => setLoading(false),
        next: (r) => {
          setLoading(false);
          setReport(ManageReport.fromReport(r));
        },
        error: (err) => {
          setLoading(false);
          enqueueSnackbar(err.text, err.options);

        },
      });
    }
  }, [reportId, enqueueSnackbar]);

  if (!isCreation && !report?.id) return null;

  const handleSave = () => {
    if (isCreation) {
      setLoading(true);
      reportsService.generateReport(ManageReport.fromTableFilter(newFilters, from, to), newFiltersDate).pipe().subscribe({
        next: () => {
          handleClose();
          enqueueSnackbar('reports.message.creation.success', {variant: 'success'});
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    } else {
      //TODO; DOES IS STILL UTILIZABLE
      // if (!reportId) return;
      // setLoading(true);
      // reportsService.updateReport(reportId, report).pipe(finalize(() => setLoading(false))).subscribe({
      //   next: (report) => {
      //     enqueueSnackbar('Votre rapport a été mis à jour avec succès!', {variant: 'success'}); //todo trac key
      //     setReport(ManageReport.fromReport(report));
      //   },
      //   error: (err) => enqueueSnackbar(err.text, err.options),
      // });
    }
  };


  const errors = {
    clientOrganisation: getFieldError(newFilters[0]?.values ?? [], FieldValidationType.REQUIRED_ARRAY),
    startDate: getFieldError(from, FieldValidationType.REQUIRED_DATE),
    endDate: getFieldError(to, FieldValidationType.REQUIRED_DATE),
  };

  return (
    <Drawer anchor="right" open onClose={handleClose}>
      <Stack overflow="hidden" flex={1}>
        <Stack direction="row" justifyContent="flex-end">
          <IconButton onClick={handleClose}>
            <Close/>
          </IconButton>
        </Stack>
        <Stack pr="10px">
          <Stack direction="row" alignItems="center" spacing={1.5} pb="8px">
            <Typography fontSize={24} fontWeight="700">
              {t('reports.creation.title')}
            </Typography>
          </Stack>
          <Divider variant="fullWidth" orientation="horizontal"/>
        </Stack>
        <Stack className="scrollable" flex={1} overflow="auto" pb="24px" pr="10px">
          <Stack py="20px" spacing={2}>
            <Typography fontSize={'18px'} fontWeight="700">
              {t('reports.creation.details')}
            </Typography>
            <Stack spacing={2}>
              <AsyncSelectWithSearchComponent
                labelWidth={Sizes.drawerInputLabelWidth}
                label={t(`global.filterFieldType.${newFilters[0]?.type}`)}
                placeholder={t('global.selectOption')}
                handleChange={(value) => {
                  updateFilters('values', 0)(value ? [value] : []);
                  return;
                }}
                value={newFilters[0]?.values?.length === 1 ? newFilters[0]?.values[0] : undefined}
                error={errors.clientOrganisation}
                getOptions={(search) => {
                  return clientsService.searchClients(search).pipe(
                    map((options) => options.data.map((o) => ({label: o.name || '-', value: o.id})))
                  );
                }}
                required
              />
            </Stack>
            <Divider variant="fullWidth" orientation="horizontal"/>
            <Stack py="20px" spacing={2}>
              <Typography fontSize={'18px'} fontWeight="700">
                {t('reports.creation.timelineTitle')}
              </Typography>
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <InputLabel error={errors.startDate || errors.endDate} sx={{width: Sizes.drawerInputLabelWidth}}>
                  {t('reports.creation.timelineDate')}*
                </InputLabel>
                <DateRangePickerComponent
                  label={t('clients.details.contractedServices.manage.fixPrices.fromTo')}
                  startDate={from}
                  endDate={to}
                  handleChangeStartDate={(from) => {
                    setFrom(from);
                  }}
                  handleChangeEndDate={(to) => {
                    setTo(to);
                  }}
                  error={errors.startDate || errors.endDate}/>
              </Stack>
            </Stack>
            <Stack justifyContent={'space-between'} spacing={4} flex={1}>
              <Divider variant="fullWidth" orientation="horizontal"/>
              <Box alignSelf="flex-end">
                <Button variant="contained" color="primary" disabled={loading || checkIfErrors(errors)}
                        onClick={handleSave}>
                  {t('reports.creation.generateButton')}
                </Button>
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default ManageGenerateReportDrawer;
